import type { DisciplineVariant, ShotResult } from '../types';

// Full skeet station sequence: 25 shots (24 + option shot)
// Stations 1,2,6,7 get doubles; stations 3,4,5,8 only singles
export const SKEET_STATION_SEQUENCE: Array<{ station: number; house: 'high' | 'low' | 'doubles' }> = [
  // Station 1: high, low, doubles (2 birds) = 4 shots
  { station: 1, house: 'high' },
  { station: 1, house: 'low' },
  { station: 1, house: 'doubles' }, // records 2 shots
  // Station 2: high, low, doubles (2 birds) = 4 shots
  { station: 2, house: 'high' },
  { station: 2, house: 'low' },
  { station: 2, house: 'doubles' },
  // Station 3: high, low = 2 shots
  { station: 3, house: 'high' },
  { station: 3, house: 'low' },
  // Station 4: high, low = 2 shots
  { station: 4, house: 'high' },
  { station: 4, house: 'low' },
  // Station 5: high, low = 2 shots
  { station: 5, house: 'high' },
  { station: 5, house: 'low' },
  // Station 6: high, low, doubles (2 birds) = 4 shots
  { station: 6, house: 'high' },
  { station: 6, house: 'low' },
  { station: 6, house: 'doubles' },
  // Station 7: high, low, doubles (2 birds) = 4 shots
  { station: 7, house: 'high' },
  { station: 7, house: 'low' },
  { station: 7, house: 'doubles' },
  // Station 8: high, low = 2 shots
  { station: 8, house: 'high' },
  { station: 8, house: 'low' },
  // Shot 25 = option shot (repeat first miss or low house 8)
  { station: 8, house: 'low' },
];

/**
 * Calculate the current station for trap disciplines (rotate right).
 * startStation is 1-indexed, stationCount is typically 5.
 */
export function calculateTrapStation(
  totalShots: number,
  shotsPerStation: number,
  startStation: number,
  stationCount: number
): number {
  const stationIndex = Math.floor(totalShots / shotsPerStation);
  let station = startStation + stationIndex;
  while (station > stationCount) station -= stationCount;
  return station;
}

/**
 * Calculate the shot number within the current station (1-indexed).
 */
export function calculateShotNumber(totalShots: number, shotsPerStation: number): number {
  return (totalShots % shotsPerStation) + 1;
}

export interface ShootingPosition {
  station: number;
  shotNumber: number;
  isDoubles: boolean;
}

export function getShootingPosition(
  variant: DisciplineVariant,
  totalShots: number,
  shotsPerStation: number,
  startStation: number,
  stationCount: number
): ShootingPosition {
  if (variant === 'american-skeet') {
    const entry = getSkeetSequenceEntry(totalShots);
    return {
      station: entry.station,
      shotNumber: totalShots + 1,
      isDoubles: isDoublesShot(variant, totalShots),
    };
  }

  return {
    station: calculateTrapStation(totalShots, shotsPerStation, startStation, stationCount),
    shotNumber: calculateShotNumber(totalShots, shotsPerStation),
    isDoubles: isDoublesShot(variant, totalShots),
  };
}

/**
 * Get the skeet sequence entry for a given shot index.
 * The index maps into SKEET_STATION_SEQUENCE. Doubles entries represent
 * a single "pull" that produces 2 birds.
 *
 * To find the sequence entry for a given number of completed shots,
 * we walk the sequence counting 2 shots for doubles entries.
 */
export function getSkeetSequenceEntry(shotIndex: number): { station: number; house: 'high' | 'low' | 'doubles'; sequenceIndex: number } {
  let shotsAccounted = 0;
  for (let i = 0; i < SKEET_STATION_SEQUENCE.length; i++) {
    const entry = SKEET_STATION_SEQUENCE[i];
    const shotsForEntry = entry.house === 'doubles' ? 2 : 1;
    if (shotIndex < shotsAccounted + shotsForEntry) {
      return { ...entry, sequenceIndex: i };
    }
    shotsAccounted += shotsForEntry;
  }
  // Past end of sequence — return last entry
  const last = SKEET_STATION_SEQUENCE[SKEET_STATION_SEQUENCE.length - 1];
  return { ...last, sequenceIndex: SKEET_STATION_SEQUENCE.length - 1 };
}

/**
 * Determine if the current shot position is the START of a doubles pull.
 * Returns true only at the first bird of a doubles pair (the UI shows 4 buttons
 * and records 2 shots at once, so we skip the second bird index).
 *
 * For ATA Doubles: every pull is a pair — shots come in pairs (0,1), (2,3), etc.
 * For Skeet: doubles at stations 1, 2, 6, 7 — only at the exact start of each pair.
 */
export function isDoublesShot(variant: DisciplineVariant, shotIndex: number): boolean {
  if (variant === 'ata-doubles') {
    return shotIndex % 2 === 0;
  }
  if (variant === 'american-skeet') {
    // Walk the sequence to find if shotIndex lands exactly at the start of a doubles entry
    let shotsAccounted = 0;
    for (let i = 0; i < SKEET_STATION_SEQUENCE.length; i++) {
      const entry = SKEET_STATION_SEQUENCE[i];
      if (entry.house === 'doubles' && shotIndex === shotsAccounted) {
        return true;
      }
      shotsAccounted += entry.house === 'doubles' ? 2 : 1;
      if (shotsAccounted > shotIndex) break;
    }
    return false;
  }
  return false;
}

export type DoublesButtonType = 'both-hit' | 'first-hit' | 'second-hit' | 'both-miss';

/**
 * Convert a doubles button press into the two individual shot results.
 */
export function getDoublesShots(buttonType: DoublesButtonType): Array<{ result: ShotResult }> {
  switch (buttonType) {
    case 'both-hit':
      return [{ result: 'hit' }, { result: 'hit' }];
    case 'first-hit':
      return [{ result: 'hit' }, { result: 'miss' }];
    case 'second-hit':
      return [{ result: 'miss' }, { result: 'hit' }];
    case 'both-miss':
      return [{ result: 'miss' }, { result: 'miss' }];
  }
}

/**
 * Check if a round is complete for a single player.
 * For trap: stationCount * shotsPerStation shots.
 * For skeet: 25 shots total (fixed).
 */
export function isRoundComplete(
  totalShots: number,
  shotsPerStation: number,
  stationCount: number,
  variant: DisciplineVariant
): boolean {
  if (variant === 'american-skeet') {
    return totalShots >= 25;
  }
  return totalShots >= shotsPerStation * stationCount;
}

export function getTargetsPerRound(
  variant: DisciplineVariant,
  shotsPerStation: number,
  stationCount: number
): number {
  return variant === 'american-skeet' ? 25 : shotsPerStation * stationCount;
}

export function getSubEventTargetCounts(
  variant: DisciplineVariant,
  shotsPerStation: number,
  stationCount: number
): number[] {
  if (variant === 'american-skeet') {
    return [4, 8, 10, 12, 14, 18, 22, 25];
  }

  return Array.from(
    { length: stationCount },
    (_, index) => (index + 1) * shotsPerStation
  );
}

export function isSubEventComplete(
  totalShotsAllPlayers: number,
  playerCount: number,
  variant: DisciplineVariant,
  shotsPerStation: number,
  stationCount: number
): boolean {
  if (playerCount <= 0 || totalShotsAllPlayers <= 0) return false;
  if (totalShotsAllPlayers % playerCount !== 0) return false;

  const shotsPerPlayer = totalShotsAllPlayers / playerCount;
  return getSubEventTargetCounts(variant, shotsPerStation, stationCount)
    .includes(shotsPerPlayer);
}

export function getNextRoundNumber(roundNumbers: number[]): number {
  return roundNumbers.length === 0 ? 1 : Math.max(...roundNumbers) + 1;
}

/**
 * Check if a rotation (one pass through all players) is complete.
 * Each player shoots shotsPerStation shots before rotating.
 */
export function isRotationComplete(
  totalShotsAllPlayers: number,
  playerCount: number,
  shotsPerStation: number
): boolean {
  if (playerCount === 0 || shotsPerStation === 0) return false;
  const shotsPerRotation = playerCount * shotsPerStation;
  return totalShotsAllPlayers > 0 && totalShotsAllPlayers % shotsPerRotation === 0;
}

/**
 * For ATA Doubles: each pull launches 2 birds, so 5 pulls per station = 10 shots.
 * This returns the number of pulls (button presses) per station.
 */
export function getDoublesPerStation(shotsPerStation: number): number {
  return Math.floor(shotsPerStation / 2);
}
